import { splitIdentifiedTitle } from '@src/utility/speakerIdentification';
import {
  deriveSpeakerName,
  deriveSpeakerTitle,
  splitSpeakerName,
} from '@src/utility/speakerRole';

import type { SpeakerData } from '@src/services/SpeakerManagementService';
import type { IdentifiedSpeaker } from '@src/utility/speakerIdentification';

/**
 * What ID Speaker's "Create New Speaker…" and Edit Speaker open the full
 * speaker modal with (PLAT-12257): `SpeakerFormModal`'s `speakerToEdit`, with
 * no Dato id, so it reads "Create & Assign Speaker", offers every field and
 * the headshot, and hands the record back rather than saving it. The speaker
 * is created on Apply, like every speaker the review creates.
 */
export interface SpeakerFormPrefill {
  speakerLabel: string;
  datoSpeakerId: '';
  name: string;
  firstName: string;
  lastName: string;
  title: string;
  role: string;
  organization: string;
  bio?: string;
  voice?: string;
  gender?: SpeakerData['gender'] | null;
  headshot?: string | null;
  headshots?: Array<{ id: string; url: string }>;
}

/** A blank speaker for a row: "Create New Speaker…". */
export function blankSpeakerPrefill(speakerLabel: string): SpeakerFormPrefill {
  return {
    speakerLabel,
    datoSpeakerId: '',
    name: '',
    firstName: '',
    lastName: '',
    title: '',
    role: '',
    organization: '',
  };
}

/**
 * The modal prefilled from what the review has for a row (Edit Speaker).
 *
 * - A speaker described in the modal before reopens as it was saved.
 * - Otherwise name parts are the model's when it gave them; failing that the
 *   name is split only when it splits cleanly (`splitSpeakerName`), else it
 *   all goes in First Name for the person to divide, since they see it.
 * - The model's title is split on its first "|" as Apply would (PLAT-12180);
 *   a title with no "|" is a Position. Title is left empty, so derived.
 */
export function speakerFormPrefill(
  candidate: IdentifiedSpeaker,
): SpeakerFormPrefill {
  const { speakerLabel, draft } = candidate;
  if (draft) {
    return {
      speakerLabel,
      datoSpeakerId: '',
      name: draft.name,
      firstName: draft.firstName ?? '',
      lastName: draft.lastName ?? '',
      title: draft.title ?? '',
      role: draft.role ?? '',
      organization: draft.organization ?? '',
      bio: draft.bio,
      voice: draft.voice,
      gender: draft.gender || null,
      headshot: draft.headshots?.[0]?.url ?? null,
      headshots: draft.headshots ?? [],
    };
  }
  const given = {
    firstName: candidate.firstName?.trim() ?? '',
    lastName: candidate.lastName?.trim() ?? '',
  };
  const name =
    given.firstName || given.lastName
      ? given
      : (splitSpeakerName(candidate.name) ?? {
          firstName: candidate.name.trim(),
          lastName: '',
        });
  const { title, role, organization } = splitIdentifiedTitle(candidate.title);
  return {
    speakerLabel,
    datoSpeakerId: '',
    // The name the review showed, so the modal reads it as derived from the
    // parts or, where the model's parts cover less of it, as typed.
    name: candidate.name.trim() || deriveSpeakerName(name),
    ...name,
    title: '',
    role: role ?? title ?? '',
    organization: organization ?? '',
  };
}

/**
 * A speaker saved in the modal as the review's candidate for a row: the same
 * shape an identification has, so the row label, the library match and
 * Apply's grouping read "First Last" and "Company | Position" as they always
 * have. The whole record rides along in `draft` for Apply to create.
 */
export function identifiedFromDraft(
  speakerLabel: string,
  draft: SpeakerData,
): IdentifiedSpeaker {
  return {
    speakerLabel,
    name: draft.name,
    title: draft.title || deriveSpeakerTitle(draft),
    ...(draft.firstName ? { firstName: draft.firstName } : {}),
    ...(draft.lastName ? { lastName: draft.lastName } : {}),
    draft,
  };
}
